import "./index.css";

import type { ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent, ReactElement } from "react";
import { useState } from "react";

import { getSanitizedHtml } from "../../handlers/utilityFunctions";
import SearchableSelect from "../SearchableSelect";
import Tips, { type TipsContent } from "../Tips";
import { Label } from "../ui/label";

interface AutoSuggestionProps {
	suggestions: string[];
	userInput: string;
	setUserInput: (value: string) => void;
	/**
	 * Rendering mode:
	 * - `true`: labeled searchable single-select (shadcn Dropdown) — the resume
	 *   builder and expert-help forms. Value is picked from `suggestions`.
	 * - `undefined`: legacy icon input-group typeahead (Bootstrap routes).
	 * - `false`: legacy search input-group typeahead (Bootstrap routes).
	 */
	isForm?: boolean;
	name?: string;
	type?: string;
	placeholder?: string;
	error?: string | null;
	label?: string;
	icon?: string;
	tips?: TipsContent | null;
}

const AutoSuggestion = (props: AutoSuggestionProps) => {
	const {
		suggestions,
		userInput,
		setUserInput,
		isForm,
		name,
		type,
		placeholder,
		error,
		label,
		icon,
		tips = null,
	} = props;

	let suggestionsListComponent: ReactElement | undefined;
	const [activeSuggestion, setActiveSuggestion] = useState(0);
	// The suggestions that match the user's input
	const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
	// Whether or not the suggestion list is shown
	const [showSuggestions, setShowSuggestions] = useState(false);
	// What the user has entered

	const onChange = (e: ChangeEvent<HTMLInputElement>) => {
		const userInput = e.currentTarget.value;

		setFilteredSuggestions(
			suggestions.filter((suggestion) => suggestion.toLowerCase().indexOf(userInput.toLowerCase()) > -1),
		);

		setActiveSuggestion(0);

		setShowSuggestions(true);
		setUserInput(e.currentTarget.value);
	};

	const onBlur = (_e: FocusEvent<HTMLInputElement>) => {
		setTimeout(() => {
			setShowSuggestions(false);
		}, 1000);
	};

	const onClick = (e: MouseEvent<HTMLLIElement>) => {
		setActiveSuggestion(0);
		setFilteredSuggestions([]);
		setShowSuggestions(false);
		setUserInput(e.currentTarget.innerText);
	};

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		// User pressed the enter key
		if (e.keyCode === 13) {
			setActiveSuggestion(0);
			setShowSuggestions(false);
			setUserInput(filteredSuggestions[activeSuggestion]);
		}
		// User pressed the up arrow
		else if (e.keyCode === 38) {
			if (activeSuggestion === 0) {
				return;
			}

			setActiveSuggestion(activeSuggestion - 1);
		}
		// User pressed the down arrow
		else if (e.keyCode === 40) {
			if (activeSuggestion - 1 === filteredSuggestions.length) {
				return;
			}

			setActiveSuggestion(activeSuggestion + 1);
		}
	};

	if (showSuggestions && userInput) {
		if (filteredSuggestions.length) {
			suggestionsListComponent = (
				<ul className="suggestions">
					{filteredSuggestions.map((suggestion, index) => {
						let className: string | undefined;

						// Flag the active suggestion with a class
						if (index === activeSuggestion) {
							className = "suggestion-active";
						}

						return (
							<li className={className} key={index} onClick={onClick}>
								{suggestion}
							</li>
						);
					})}
				</ul>
			);
		} else {
			suggestionsListComponent = (
				<ul className="no-suggestions">
					<em>No suggestions, you&#39;re on your own!</em>
				</ul>
			);
		}
	}

	return (
		<>
			{isForm ? (
				// Searchable single-select — the same SearchableSelect the onboarding screen uses.
				// Replaces the free-text typeahead so builder fields (district/region,
				// language) are picked from their controlled vocabulary.
				<div className="relative space-y-1">
					<div className="flex items-center justify-between">
						{label && <Label className="text-sm">{label}</Label>}
						{tips && <Tips content={tips} />}
					</div>
					<SearchableSelect
						search
						options={suggestions}
						value={userInput}
						onChange={setUserInput}
						placeholder={placeholder ?? "Type to search"}
						name={name}
						aria-invalid={!!error}
					/>
					{error && <div className="text-sm font-medium leading-tight text-red-500">{getSanitizedHtml(error)}</div>}
				</div>
			) : isForm === undefined ? (
				<div className="input-group mb-3 mb-lg-0 border rounded">
					<div className="input-group-prepend bg-white d-flex align-items-center p-2">
						<span className={`${icon} text-primary`} />
					</div>
					<input
						type={type}
						className="form-control  pl-0 border-0"
						placeholder={placeholder}
						name={name}
						value={userInput}
						onChange={onChange}
						onBlur={onBlur}
					/>
				</div>
			) : (
				<div className="flex">
					<input
						type="text"
						value={userInput}
						onBlur={onBlur}
						onChange={onChange}
						onKeyDown={onKeyDown}
						placeholder="Search"
						className="flex h-9 w-full rounded-l-md border border-r-0 bg-transparent px-3 py-1 text-sm transition-colors focus-visible:outline-hidden"
						aria-label="Recipient&#39;s username"
						aria-describedby="basic-addon2"
					/>
					<button
						className="inline-flex cursor-pointer items-center justify-center rounded-r-md bg-primary px-4 text-primary-foreground transition-colors hover:bg-primary/90 md:px-10"
						type="button"
					>
						<span className="fa fa-search" />
					</button>
				</div>
			)}

			{suggestionsListComponent}
		</>
	);
};

export default AutoSuggestion;
